// admin/styles.ts — inline style objects for the admin portal.
//
// Deliberately separate from web/src/styles.ts (the main game surface). The
// admin look is dark utilitarian; no grain, no flicker, no theme switching.
// Same inline-styles approach as the main app for artifact portability.

import type { CSSProperties } from "react";

// ── Palette ──────────────────────────────────────────────────────────────
// Reference these via the `tokens` export from any admin component. Tweaks
// land in one place. Tokens deliberately do NOT use CSS custom properties —
// the admin surface has a single, stable theme.
export const tokens = {
  bg: "#0d0f12",
  panel: "#171a1f",
  panelAlt: "#1d2127",
  border: "#272c33",
  borderStrong: "#373d46",
  inkPrimary: "#d6dbe1",
  inkMuted: "#7a8088",
  inkDim: "#5a6068",
  accent: "#6b95ff",
  accentMuted: "rgba(107, 149, 255, 0.18)",
  destructive: "#ff7058",
  destructiveMuted: "rgba(255, 112, 88, 0.18)",
  ok: "#68c596",
  okMuted: "rgba(104, 197, 150, 0.16)",
  warn: "#e0b46c",
  warnMuted: "rgba(224, 180, 108, 0.18)",
  fontBody:
    "'Inter', 'Segoe UI', system-ui, -apple-system, BlinkMacSystemFont, sans-serif",
  fontMono:
    "'JetBrains Mono', 'SFMono-Regular', Menlo, Consolas, 'Courier New', monospace",
} as const;

export const styles: Record<string, CSSProperties> = {
  // ── Shell ──────────────────────────────────────────────────────────────
  // height: 100vh (not minHeight) is load-bearing: it pins the shell to
  // viewport height so the inner sidebar + content panes can each have
  // their own overflow-y: auto and scroll independently. A long Users
  // or Ideas table scrolling no longer drags the sidebar with it.
  shell: {
    height: "100vh",
    background: tokens.bg,
    color: tokens.inkPrimary,
    fontFamily: tokens.fontBody,
    fontSize: 14,
    lineHeight: 1.45,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  shellRow: {
    flex: 1,
    display: "flex",
    minHeight: 0,
    position: "relative",
  },
  // Top bar — fixed-height. Holds the brand mark and the signed-in admin's
  // email + sign-out.
  topbar: {
    flex: "0 0 auto",
    height: 56,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "0 24px",
    background: tokens.panel,
    borderBottom: `1px solid ${tokens.border}`,
  },
  brand: {
    display: "flex",
    alignItems: "baseline",
    gap: 12,
  },
  brandTitle: {
    fontSize: 16,
    fontWeight: 600,
    letterSpacing: "0.02em",
    color: tokens.inkPrimary,
    margin: 0,
  },
  brandSub: {
    fontSize: 12,
    color: tokens.inkMuted,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
  },
  topbarRight: {
    display: "flex",
    alignItems: "center",
    gap: 16,
    fontSize: 13,
    color: tokens.inkMuted,
  },
  // Subtle build-version indicator, sits left of the admin email. Dimmer and
  // smaller than the surrounding topbar text so it reads as metadata.
  versionTag: {
    fontSize: 11,
    color: tokens.inkMuted,
    opacity: 0.6,
    letterSpacing: "0.02em",
    whiteSpace: "nowrap",
  },
  signoutBtn: {
    background: "transparent",
    color: tokens.inkPrimary,
    border: `1px solid ${tokens.border}`,
    borderRadius: 4,
    padding: "6px 12px",
    fontSize: 13,
    cursor: "pointer",
    fontFamily: tokens.fontBody,
  },
  // ── Sidebar ────────────────────────────────────────────────────────────
  // overflowY: auto so a long nav (future) scrolls within the sidebar,
  // and so the sidebar doesn't push the content area down when the
  // viewport gets taller content elsewhere.
  sidebar: {
    flex: "0 0 220px",
    background: tokens.panel,
    borderRight: `1px solid ${tokens.border}`,
    display: "flex",
    flexDirection: "column",
    padding: "16px 12px",
    gap: 4,
    overflowY: "auto",
    minHeight: 0,
  },
  navItem: {
    display: "block",
    width: "100%",
    textAlign: "left",
    background: "transparent",
    color: tokens.inkMuted,
    border: "1px solid transparent",
    padding: "10px 14px",
    borderRadius: 4,
    fontSize: 14,
    cursor: "pointer",
    fontFamily: tokens.fontBody,
  },
  navItemActive: {
    color: tokens.inkPrimary,
    background: tokens.accentMuted,
    borderColor: tokens.accent,
  },
  // ── Content ────────────────────────────────────────────────────────────
  content: {
    flex: 1,
    minWidth: 0,
    overflow: "auto",
    padding: 32,
  },
  pageTitle: {
    fontSize: 22,
    fontWeight: 600,
    margin: "0 0 4px",
    color: tokens.inkPrimary,
    letterSpacing: "-0.01em",
  },
  pageSub: {
    fontSize: 13,
    color: tokens.inkMuted,
    margin: "0 0 24px",
  },
  // ── Login ──────────────────────────────────────────────────────────────
  loginShell: {
    minHeight: "100vh",
    background: tokens.bg,
    color: tokens.inkPrimary,
    fontFamily: tokens.fontBody,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
  },
  loginCard: {
    width: "100%",
    maxWidth: 380,
    background: tokens.panel,
    border: `1px solid ${tokens.border}`,
    borderRadius: 8,
    padding: 32,
    boxShadow: "0 14px 36px rgba(0,0,0,0.35)",
  },
  loginTitle: {
    fontSize: 18,
    fontWeight: 600,
    margin: "0 0 4px",
    letterSpacing: "0.02em",
  },
  loginSub: {
    fontSize: 13,
    color: tokens.inkMuted,
    margin: "0 0 24px",
  },
  loginButton: {
    width: "100%",
    padding: "12px 16px",
    background: tokens.accent,
    color: "#fff",
    border: "none",
    borderRadius: 4,
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: tokens.fontBody,
  },
  loginError: {
    marginTop: 16,
    padding: "10px 12px",
    background: tokens.destructiveMuted,
    color: tokens.destructive,
    border: `1px solid ${tokens.destructive}`,
    borderRadius: 4,
    fontSize: 13,
  },
  loginHint: {
    marginTop: 18,
    fontSize: 12,
    color: tokens.inkMuted,
    lineHeight: 1.5,
  },
  loginLink: {
    color: tokens.accent,
    textDecoration: "none",
  },
  // ── Cards / tiles (Dashboard) ──────────────────────────────────────────
  tileGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: 16,
    marginBottom: 24,
  },
  tile: {
    background: tokens.panel,
    border: `1px solid ${tokens.border}`,
    borderRadius: 6,
    padding: 24,
  },
  tileLabel: {
    fontSize: 12,
    color: tokens.inkMuted,
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    margin: 0,
  },
  tileValue: {
    fontFamily: tokens.fontMono,
    fontSize: 28,
    fontWeight: 600,
    margin: "10px 0 4px",
    color: tokens.inkPrimary,
    letterSpacing: "-0.02em",
  },
  tileSub: {
    fontSize: 12,
    color: tokens.inkMuted,
    margin: 0,
  },
  panel: {
    background: tokens.panel,
    border: `1px solid ${tokens.border}`,
    borderRadius: 6,
    padding: 24,
  },
  panelTitle: {
    fontSize: 14,
    fontWeight: 600,
    margin: "0 0 16px",
    color: tokens.inkPrimary,
    letterSpacing: "0.01em",
  },
  panelRow: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "8px 0",
    borderTop: `1px solid ${tokens.border}`,
    fontSize: 13,
  },
  panelRowFirst: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "8px 0",
    fontSize: 13,
  },
  bar: {
    flex: 1,
    height: 6,
    background: tokens.panelAlt,
    borderRadius: 3,
    overflow: "hidden",
  },
  barFill: {
    height: "100%",
    background: tokens.accent,
  },
  // ── Users table ────────────────────────────────────────────────────────
  controlsRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
    marginBottom: 16,
    flexWrap: "wrap",
  },
  searchInput: {
    background: tokens.panel,
    color: tokens.inkPrimary,
    border: `1px solid ${tokens.border}`,
    borderRadius: 4,
    padding: "8px 12px",
    fontSize: 14,
    width: 320,
    fontFamily: tokens.fontBody,
  },
  select: {
    background: tokens.panel,
    color: tokens.inkPrimary,
    border: `1px solid ${tokens.border}`,
    borderRadius: 4,
    padding: "8px 10px",
    fontSize: 13,
    fontFamily: tokens.fontBody,
  },
  tableWrap: {
    background: tokens.panel,
    border: `1px solid ${tokens.border}`,
    borderRadius: 6,
    overflow: "auto",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 13,
  },
  th: {
    textAlign: "left",
    padding: "10px 14px",
    fontWeight: 600,
    color: tokens.inkMuted,
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    background: tokens.panel,
    borderBottom: `1px solid ${tokens.border}`,
    position: "sticky",
    top: 0,
    zIndex: 1,
    whiteSpace: "nowrap",
    cursor: "default",
    userSelect: "none",
  },
  thSortable: {
    cursor: "pointer",
  },
  thActive: {
    color: tokens.accent,
  },
  td: {
    padding: "10px 14px",
    borderBottom: `1px solid ${tokens.border}`,
    color: tokens.inkPrimary,
    verticalAlign: "middle",
  },
  tdMono: {
    fontFamily: tokens.fontMono,
    fontSize: 12.5,
    color: tokens.inkPrimary,
  },
  tdMuted: {
    color: tokens.inkMuted,
  },
  pill: {
    display: "inline-block",
    padding: "2px 8px",
    fontSize: 11,
    borderRadius: 999,
    border: `1px solid ${tokens.border}`,
    color: tokens.inkMuted,
    background: tokens.panelAlt,
    textTransform: "lowercase",
    letterSpacing: "0.02em",
  },
  pillActive: {
    color: tokens.ok,
    background: tokens.okMuted,
    borderColor: tokens.ok,
  },
  pillWarn: {
    color: tokens.warn,
    background: tokens.warnMuted,
    borderColor: tokens.warn,
  },
  pillTrial: {
    color: tokens.accent,
    background: tokens.accentMuted,
    borderColor: tokens.accent,
  },
  pillGrey: {
    color: tokens.inkMuted,
    background: tokens.panelAlt,
    borderColor: tokens.border,
  },
  kebab: {
    background: "transparent",
    border: "none",
    color: tokens.inkMuted,
    fontSize: 18,
    cursor: "pointer",
    padding: "2px 8px",
    borderRadius: 4,
    fontFamily: tokens.fontBody,
  },
  menu: {
    position: "absolute",
    right: 0,
    top: "100%",
    background: tokens.panelAlt,
    border: `1px solid ${tokens.borderStrong}`,
    borderRadius: 4,
    padding: 6,
    minWidth: 240,
    boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
    zIndex: 10,
  },
  menuItem: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    background: "transparent",
    color: tokens.inkPrimary,
    border: "none",
    padding: "8px 10px",
    fontSize: 13,
    cursor: "pointer",
    textAlign: "left",
    borderRadius: 3,
    fontFamily: tokens.fontBody,
  },
  menuItemDanger: {
    color: tokens.destructive,
  },
  menuItemDisabled: {
    color: tokens.inkDim,
    cursor: "not-allowed",
  },
  // ── Pagination ─────────────────────────────────────────────────────────
  pager: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
    marginTop: 16,
    fontSize: 13,
    color: tokens.inkMuted,
  },
  pagerBtn: {
    background: tokens.panel,
    color: tokens.inkPrimary,
    border: `1px solid ${tokens.border}`,
    borderRadius: 4,
    padding: "6px 12px",
    fontSize: 13,
    cursor: "pointer",
    fontFamily: tokens.fontBody,
  },
  pagerBtnDisabled: {
    color: tokens.inkDim,
    cursor: "not-allowed",
  },
  // ── Toast ──────────────────────────────────────────────────────────────
  toastStack: {
    position: "fixed",
    bottom: 24,
    right: 24,
    display: "flex",
    flexDirection: "column",
    gap: 8,
    zIndex: 1000,
  },
  toast: {
    background: tokens.panelAlt,
    color: tokens.inkPrimary,
    border: `1px solid ${tokens.borderStrong}`,
    borderRadius: 4,
    padding: "10px 14px",
    fontSize: 13,
    boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
    maxWidth: 360,
  },
  toastError: {
    borderColor: tokens.destructive,
    color: tokens.destructive,
    background: tokens.destructiveMuted,
  },
  // ── Misc ───────────────────────────────────────────────────────────────
  empty: {
    padding: 32,
    textAlign: "center",
    color: tokens.inkMuted,
    fontSize: 13,
  },
  spinner: {
    color: tokens.inkMuted,
    fontSize: 13,
  },
};
